import { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';

const FALLBACK_STATUSES = [
  { code: 'NEW', label: 'New', badge_tone: 'slate', is_default: true },
  { code: 'CALL', label: 'Call', badge_tone: 'sky' },
  { code: 'VISIT', label: 'Visit', badge_tone: 'violet' },
  { code: 'DEMO', label: 'Demo', badge_tone: 'amber' },
  { code: 'SALE', label: 'Sale', badge_tone: 'emerald' },
];

const FALLBACK_PARTNER_STATUSES = [
  { code: 'NEW', label: 'New', badge_tone: 'slate', is_default: true },
  { code: 'CALL', label: 'Call', badge_tone: 'sky' },
  { code: 'INTERVIEW', label: 'Interviewed', badge_tone: 'violet' },
  { code: 'NOT_INTERESTED', label: 'Not interested', badge_tone: 'rose' },
];

export function useProspectConfig(companyId = null) {
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchConfig = useCallback(async () => {
    try {
      setLoading(true);
      const requestConfig = { withCredentials: true };
      if (companyId) {
        requestConfig.params = { company_id: companyId };
      }
      const { data } = await axios.get('/api/system/prospect-config', requestConfig);
      setConfig(data);
      setError(null);
    } catch (err) {
      const status = err?.response?.status;
      // 400 = no company context (platform admin); fallbacks below are enough.
      if (status !== 400 && status !== 401) {
        console.error('Failed to load prospect config:', err);
      }
      setError(err);
      setConfig(null);
    } finally {
      setLoading(false);
    }
  }, [companyId]);

  useEffect(() => {
    fetchConfig();
  }, [fetchConfig]);

  const prospectStatuses = useMemo(
    () => config?.prospect_statuses?.length ? config.prospect_statuses : FALLBACK_STATUSES,
    [config]
  );

  const partnerStatuses = useMemo(
    () => config?.partner_statuses?.length ? config.partner_statuses : FALLBACK_PARTNER_STATUSES,
    [config]
  );

  const defaultStatus = config?.default_status || 'NEW';
  const partnerDefaultStatus = config?.partner_default_status || 'NEW';

  const statusCodes = useMemo(
    () => prospectStatuses.map((s) => s.code),
    [prospectStatuses]
  );

  return {
    config,
    loading,
    error,
    prospectStatuses,
    partnerStatuses,
    defaultStatus,
    partnerDefaultStatus,
    statusCodes,
    refresh: fetchConfig,
  };
}

export { FALLBACK_STATUSES, FALLBACK_PARTNER_STATUSES };
